import { setOptions, importLibrary } from "@googlemaps/js-api-loader";
import { MarkerClusterer, SuperClusterAlgorithm } from "@googlemaps/markerclusterer";

let optionsSet = false;

/**
 * Google Maps with Advanced Markers, optional clustering and filtering.
 * Markers carry an HTML element, so their look is styled in SCSS.
 */
export class GoogleMap {
    constructor(config = {}) {
        this.config = {
            apiKey: "",
            mapId: "DEMO_MAP_ID",
            center: { lat: 51.6, lng: 5.3 },
            zoom: 9,
            maxZoom: 15,
            padding: 50,
            clustering: false,
            clusterRadius: 100,
            gestureHandling: "cooperative",
            ...config,
        };

        this.map = null;
        this.markers = [];
        this.clusterer = null;
        this.activeMarker = null;
    }

    async init(container) {
        if (!optionsSet) {
            setOptions({ key: this.config.apiKey, v: "weekly" });
            optionsSet = true;
        }

        const [{ Map }, { AdvancedMarkerElement }] = await Promise.all([
            importLibrary("maps"),
            importLibrary("marker"),
        ]);
        this.AdvancedMarkerElement = AdvancedMarkerElement;

        this.map = new Map(container, {
            center: this.config.center,
            zoom: this.config.zoom,
            mapId: this.config.mapId,
            gestureHandling: this.config.gestureHandling,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            cameraControl: false,
        });

        return this.map;
    }

    addMarker({ position, title = "", content = null, data = {}, onClick = null }) {
        if (!Number.isFinite(position?.lat) || !Number.isFinite(position?.lng)) return null;

        const marker = new this.AdvancedMarkerElement({
            map: this.config.clustering ? null : this.map,
            position,
            title,
            content,
            gmpClickable: true,
        });

        marker.data = data;
        marker.visible = true;

        if (onClick) {
            marker.addEventListener("gmp-click", () => onClick(marker));
        }

        this.markers.push(marker);
        return marker;
    }

    addMarkers(items) {
        const added = items.map((item) => this.addMarker(item)).filter(Boolean);
        this.render();
        return added;
    }

    render() {
        const visible = this.getVisibleMarkers();

        if (this.config.clustering) {
            this.clusterer?.clearMarkers();
            this.clusterer = new MarkerClusterer({
                map: this.map,
                markers: visible,
                algorithm: new SuperClusterAlgorithm({ minPoints: 2, radius: this.config.clusterRadius }),
                renderer: { render: (cluster) => this.renderCluster(cluster) },
            });
        } else {
            this.markers.forEach((marker) => {
                marker.map = marker.visible ? this.map : null;
            });
        }

        this.fitBounds();
    }

    renderCluster({ count, position }) {
        const element = document.createElement("div");
        element.className = "map-cluster";
        element.textContent = count;

        return new this.AdvancedMarkerElement({ position, content: element, zIndex: 1000 + count });
    }

    setActive(marker, { pan = true } = {}) {
        this.activeMarker?.content?.classList.remove("is-active");
        this.activeMarker = marker;

        if (!marker) return;

        marker.content?.classList.add("is-active");
        marker.zIndex = 1000;

        if (pan) {
            this.map.panTo(marker.position);
        }
    }

    findMarker(key, value) {
        return this.markers.find((marker) => marker.data[key] === value) || null;
    }

    filterMarkers(filterFn) {
        this.markers.forEach((marker) => {
            marker.visible = filterFn(marker);
        });
        this.render();
    }

    filterByData(key, values) {
        const list = Array.isArray(values) ? values : [values];

        this.filterMarkers((marker) => {
            if (!list.length) return true;
            const value = marker.data[key];
            return Array.isArray(value) ? value.some((item) => list.includes(item)) : list.includes(value);
        });
    }

    showAllMarkers() {
        this.filterMarkers(() => true);
    }

    getVisibleMarkers() {
        return this.markers.filter((marker) => marker.visible);
    }

    fitBounds() {
        const visible = this.getVisibleMarkers();

        if (!visible.length) {
            this.map.setCenter(this.config.center);
            this.map.setZoom(this.config.zoom);
            return;
        }

        const bounds = new google.maps.LatLngBounds();
        visible.forEach((marker) => bounds.extend(marker.position));
        this.map.fitBounds(bounds, this.config.padding);

        google.maps.event.addListenerOnce(this.map, "idle", () => {
            if (this.map.getZoom() > this.config.maxZoom) {
                this.map.setZoom(this.config.maxZoom);
            }
        });
    }

    destroy() {
        this.clusterer?.clearMarkers();
        this.markers.forEach((marker) => {
            marker.map = null;
        });
        this.markers = [];
        this.map = null;
    }
}
